import React, { useEffect, useState } from 'react'
import Avatar from './Avatar'
import { MentionSuggestion } from '../hooks/useMentionSuggestions'

interface MentionSuggestionListProps {
  suggestions: MentionSuggestion[]
  onSelect: (username: string) => void
}

/** 한 줄 높이. 글꼴 계산에 맡기지 않고 고정해, 몇 줄이 들어가는지 정확히 셀 수 있게 한다. */
const ROW_HEIGHT = 52

/** 화면이 넉넉해도 이보다 많이 보여주지는 않는다 */
const MAX_ROWS = 4

/** 화면이 아무리 좁아도 이 정도는 보여준다 (한 줄만 뜨면 고를 게 없어 보인다) */
const MIN_ROWS = 2

/**
 * 목록이 차지해도 되는 화면 비율.
 * 나머지는 앱바·댓글·입력창 몫이다. 목록이 화면을 다 먹으면 무슨 댓글에
 * 답하는 중이었는지 안 보인다.
 */
const VIEWPORT_SHARE = 0.4

/**
 * 지금 화면에 몇 줄까지 넣을 수 있는지.
 *
 * visualViewport는 키보드가 올라오면 그만큼 줄어든다. 언급 목록은 항상 키보드가
 * 올라온 상태에서만 뜨므로, 이 값을 봐야 실제로 남은 공간을 알 수 있다.
 * (window.innerHeight는 iOS 웹뷰에서 키보드가 떠도 그대로라 쓸 수 없다)
 *
 * 줄 수로 끊는 이유: 높이를 화면 비율로 그냥 잘라내면 마지막 줄이 어중간하게
 * 걸려 잘린다. 들어갈 수 있는 줄 수를 세서 그만큼만 보여준다.
 */
function useVisibleRows(): number {
  const [rows, setRows] = useState(MAX_ROWS)

  useEffect(() => {
    const sync = () => {
      const viewport = window.visualViewport
      const available = viewport ? viewport.height : window.innerHeight
      const fits = Math.floor((available * VIEWPORT_SHARE) / ROW_HEIGHT)
      setRows(Math.max(MIN_ROWS, Math.min(MAX_ROWS, fits)))
    }

    sync()

    const viewport = window.visualViewport
    window.addEventListener('resize', sync)
    viewport?.addEventListener('resize', sync)

    return () => {
      window.removeEventListener('resize', sync)
      viewport?.removeEventListener('resize', sync)
    }
  }, [])

  return rows
}

/**
 * 댓글 입력창 위에 뜨는 언급(@) 추천 목록.
 *
 * 입력창 바로 위에 붙어 키보드에 가리지 않게 한다.
 * 후보가 없으면 아무것도 그리지 않는다 — 맞팔이 아닌 아이디를 치는 중이면
 * 빈 상자가 떠 있는 것보다 그냥 사라지는 편이 자연스럽다.
 *
 * 카드가 아니라 '면'으로 그린다
 * ---------------------------
 * 흰 배경에 테두리를 두른 카드로 두면 크림색 화면 위에 혼자 떠 있어 이질적이다.
 * 입력바와 같은 배경색으로 좌우를 꽉 채우고 테두리·구분선을 없애, 입력 영역이
 * 그대로 위로 늘어난 것처럼 보이게 한다.
 * 좌우 여백(20px)은 댓글 목록과 같은 값이라 아바타 위치가 세로로 맞는다.
 */
const MentionSuggestionList: React.FC<MentionSuggestionListProps> = ({
  suggestions,
  onSelect,
}) => {
  const visibleRows = useVisibleRows()

  if (suggestions.length === 0) {
    return null
  }

  return (
    <div
      className="w-full overflow-y-auto scrollbar-hide bg-ivoryKey"
      // 줄 단위로 끊어 마지막 줄이 반쯤 걸리지 않게 한다
      style={{ maxHeight: visibleRows * ROW_HEIGHT }}
    >
      {suggestions.map((user) => (
        <button
          key={user.id}
          type="button"
          // 입력창의 포커스가 풀리면 커서 위치를 잃어 삽입 위치가 어긋난다.
          // 탭으로 포커스가 넘어가기 전에 미리 막는다.
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={() => onSelect(user.username)}
          className="w-full h-52px flex items-center gap-10px px-20px bg-transparent border-none cursor-pointer text-left"
        >
          <Avatar
            nickname={user.nickname}
            profileImage={user.profileImage}
            size={32}
          />
          <div className="flex-1 min-w-0">
            <p className="font-pretendard font-semibold text-14px leading-125% text-brown-80%-main text-ellipsis-1">
              {user.username}
            </p>
            <p className="font-pretendard font-regular text-12px leading-125% text-textColorText3 text-ellipsis-1">
              {user.nickname}
            </p>
          </div>
        </button>
      ))}
    </div>
  )
}

export default MentionSuggestionList
